import { useEffect, useMemo, useState } from 'react';
import type { PublicProduct } from '../../shared/types';
import { cartLineId } from '../../shared/cart';
import { productRequiresVisualConfiguration } from '../../shared/product-configurability';
import { Badge, Button, Card, EmptyState, LoadingScreen, PageHeader, Textarea } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useBusinessBranches, useOpenWaiterAccount, usePublicTenant, useSubmitWaiterRound, useWaiterAccount, useWaiterTables } from '../hooks/businessData';
import { formatUsd } from '../lib/currency';

interface DraftLine {
  lineId: string;
  productId: string;
  productName: string;
  quantity: number;
  variantId: string | null;
  configuration: {
    productId: string;
    variantId?: string | null;
    groups: Array<{ groupId: string; options: Array<{ optionId: string; quantity: number }> }>;
    excludedIngredientIds: string[];
    includedIngredientIds: string[];
    comboComponents: [];
  };
}

function defaultConfiguration(product: PublicProduct, branchId: string): DraftLine['configuration'] | null {
  if (product.type === 'combo') return null;
  if (productRequiresVisualConfiguration(product) && product.priceMode !== 'variante') return null;
  const variantId = product.priceMode === 'variante'
    ? product.variants.find((variant) => !variant.unavailableBranchIds.includes(branchId))?.id ?? null
    : null;
  if (product.priceMode === 'variante' && !variantId) return null;
  if (product.optionGroups.length || product.ingredients.some((ingredient) => ingredient.selectable)) return null;
  return { productId: product.id, variantId, groups: [], excludedIngredientIds: [], includedIngredientIds: [], comboComponents: [] };
}

function storageKey(businessId: string | null | undefined, branchId: string, userId: string, accountId: string | null) {
  return `menucloud.waiterDraft.${businessId ?? 'business'}.${branchId}.${userId}.${accountId ?? 'no-account'}`;
}

export default function WaiterPage() {
  const { user } = useAuth();
  const { show } = useToast();
  const canChooseBranch = user?.role === 'owner' || user?.role === 'manager';
  const { data: branches = [] } = useBusinessBranches(canChooseBranch);
  const [selectedBranchId, setSelectedBranchId] = useState(user?.branchId ?? '');
  const branchId = user?.branchId ?? selectedBranchId;
  const actorId = user?.employeeId ?? user?.userId ?? 'manager';
  const { data: tenant, isLoading: tenantLoading } = usePublicTenant();
  const { data: tables = [], isLoading: tablesLoading } = useWaiterTables(branchId);
  const openAccount = useOpenWaiterAccount();
  const submitRound = useSubmitWaiterRound();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [notes, setNotes] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => crypto.randomUUID());
  const { data: account } = useWaiterAccount(accountId);
  const key = storageKey(user?.businessId, branchId, actorId, accountId);

  useEffect(() => {
    if (!selectedBranchId && branches.length) setSelectedBranchId(branches.find((branch) => branch.active)?.id ?? branches[0].id);
  }, [branches, selectedBranchId]);

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null') as { lines?: DraftLine[]; notes?: string; idempotencyKey?: string } | null;
      setDraft(saved?.lines ?? []);
      setNotes(saved?.notes ?? '');
      setIdempotencyKey(saved?.idempotencyKey ?? crypto.randomUUID());
    } catch {
      setDraft([]);
      setNotes('');
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [key]);

  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify({ lines: draft, notes, idempotencyKey }));
  }, [draft, idempotencyKey, key, notes]);

  const products = useMemo(() => tenant?.categories.flatMap((category) => category.products) ?? [], [tenant]);
  const selectedTable = tables.find((table) => table.openAccountId === accountId || table.id === account?.tableId);

  const selectTable = async (tableId: string, openId: string | null) => {
    if (openId) {
      setAccountId(openId);
      return;
    }
    try {
      const opened = await openAccount.mutateAsync(tableId);
      setAccountId(opened.id);
      show(`Cuenta abierta para ${opened.tableName}.`, 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo abrir la cuenta.', 'error'); }
  };

  const addProduct = (product: PublicProduct) => {
    const configuration = defaultConfiguration(product, branchId);
    if (!configuration) {
      show('Este producto requiere configuración avanzada. Por ahora agregalo desde recepción o simplificá su configuración.', 'error');
      return;
    }
    const lineId = cartLineId(configuration);
    setDraft((current) => {
      const existing = current.find((line) => line.lineId === lineId);
      if (existing) return current.map((line) => line.lineId === lineId ? { ...line, quantity: Math.min(20, line.quantity + 1) } : line);
      return [...current, { lineId, productId: product.id, productName: product.name, quantity: 1, variantId: configuration.variantId ?? null, configuration }];
    });
  };

  const sendRound = async () => {
    if (!accountId || draft.length === 0) return;
    try {
      const response = await submitRound.mutateAsync({
        accountId,
        idempotencyKey,
        notes,
        items: draft.map((line) => ({ ...line.configuration, quantity: line.quantity })),
      });
      show(`Ronda enviada a cocina como pedido #${response.correlation}.`, 'success');
      setDraft([]);
      setNotes('');
      setIdempotencyKey(crypto.randomUUID());
      sessionStorage.removeItem(key);
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo enviar la ronda.', 'error'); }
  };

  if (tenantLoading || tablesLoading) return <LoadingScreen label="Abriendo Meseros…" />;
  return (
    <div>
      <PageHeader title="Meseros" description="Seleccioná una mesa, armá una ronda y enviala directo a cocina." />
      {canChooseBranch && <Card className="mb-6"><label className="grid gap-2 text-sm font-bold">Sucursal<select className="rounded-xl border px-3 py-2" value={branchId} onChange={(event) => { setSelectedBranchId(event.target.value); setAccountId(null); }}><option value="">Selecciona una sucursal</option>{branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}</select></label></Card>}
      <div className="grid gap-6 xl:grid-cols-[320px_1fr_360px]">
        <Card>
          <h2 className="text-lg font-black">Mesas</h2>
          <div className="mt-4 grid gap-2">
            {tables.map((table) => (
              <button key={table.id} type="button" onClick={() => void selectTable(table.id, table.openAccountId)} className={`rounded-xl border p-3 text-left transition ${accountId === table.openAccountId ? 'border-slate-950 bg-slate-950 text-white' : 'hover:border-slate-400'}`}>
                <div className="flex items-center justify-between gap-2"><span className="font-black">{table.name}</span>{table.openAccountId ? <Badge tone="amber">abierta</Badge> : <Badge tone="green">libre</Badge>}</div>
                <div className="mt-1 text-xs opacity-70">{table.zone || 'Sin zona'}{table.waiterName ? ` · ${table.waiterName}` : ''}</div>
              </button>
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-black">Productos</h2>
          {!accountId ? <EmptyState title="Elegí una mesa" description="La ronda se arma sobre una cuenta abierta." /> : (
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {products.map((product) => {
                const canQuickAdd = Boolean(defaultConfiguration(product, branchId));
                return (
                  <button key={product.id} type="button" onClick={() => addProduct(product)} disabled={!canQuickAdd} className="rounded-2xl border p-4 text-left transition enabled:hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50">
                    <div className="font-black">{product.name}</div>
                    <div className="mt-1 text-sm text-slate-500">{formatUsd(product.effectivePriceUsdCents)}</div>
                    {!canQuickAdd && <div className="mt-2 text-xs font-bold text-amber-700">Requiere configuración avanzada</div>}
                  </button>
                );
              })}
            </div>
          )}
        </Card>
        <Card>
          <h2 className="text-lg font-black">Ronda actual</h2>
          <p className="mt-1 text-sm text-slate-500">{selectedTable ? selectedTable.name : 'Sin mesa seleccionada'}</p>
          <div className="mt-4 grid gap-2">
            {draft.map((line) => (
              <div key={line.lineId} className="rounded-xl border p-3">
                <div className="font-bold">{line.productName}</div>
                <div className="mt-2 flex items-center gap-2"><Button variant="secondary" onClick={() => setDraft((current) => current.map((item) => item.lineId === line.lineId ? { ...item, quantity: Math.max(1, item.quantity - 1) } : item))}>−</Button><span className="w-8 text-center font-black">{line.quantity}</span><Button variant="secondary" onClick={() => setDraft((current) => current.map((item) => item.lineId === line.lineId ? { ...item, quantity: Math.min(20, item.quantity + 1) } : item))}>+</Button><Button variant="ghost" onClick={() => setDraft((current) => current.filter((item) => item.lineId !== line.lineId))}>Quitar</Button></div>
              </div>
            ))}
            {!draft.length && <EmptyState title="Ronda vacía" description="Agregá productos desde el catálogo." />}
          </div>
          <Textarea className="mt-4" placeholder="Notas para cocina" value={notes} onChange={(event) => setNotes(event.target.value)} />
          <Button className="mt-4 w-full" busy={submitRound.isPending} disabled={!accountId || !draft.length} onClick={() => void sendRound()}>Enviar a cocina</Button>
        </Card>
      </div>
    </div>
  );
}
